// src/components/ExpenseModal.tsx
import React, { useState } from 'react';
import {
  Modal, Form, Input, Select, InputNumber,
  Button, Space, Divider, Tag, Radio, App,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { apiCreateExpense, apiUpdateExpense } from '../services/expenseAPI';

const { Option } = Select;

const CATEGORIES = ['Офис', 'Логистика', 'Закупка запчастей', 'Маркетинг', 'Прочее'];

type Line = { name: string; qty: number; unitCost: number };

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialValues?: any;
};

const ExpenseModal: React.FC<Props> = ({ open, onClose, onCreated, initialValues }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<'single' | 'multi'>('single');
  const [lines, setLines] = useState<Line[]>([{ name: '', qty: 1, unitCost: 0 }]);

  // Pre-fill form when opening in edit mode
  React.useEffect(() => {
    if (open && initialValues) {
      const isMulti = Array.isArray(initialValues.lines) && initialValues.lines.length > 0;
      setMode(isMulti ? 'multi' : 'single');
      form.setFieldsValue({
        supplier:    initialValues.supplier    ?? '',
        category:    initialValues.category    ?? 'Прочее',
        status:      initialValues.status      ?? 'paid',
        description: initialValues.description ?? '',
        qty:         initialValues.qty         ?? 1,
        unitCost:    initialValues.unitCost     ?? initialValues.total ?? 0,
      });
      if (isMulti) {
        setLines(initialValues.lines.map((l: any) => ({
          name: l.name ?? '', qty: l.qty ?? 1, unitCost: l.unitCost ?? 0,
        })));
      }
    }
  }, [open, initialValues, form]);

  const handleClose = () => {
    form.resetFields();
    setMode('single');
    setLines([{ name: '', qty: 1, unitCost: 0 }]);
    onClose();
  };

  const addLine = () => setLines(prev => [...prev, { name: '', qty: 1, unitCost: 0 }]);
  const removeLine = (i: number) => setLines(prev => prev.filter((_, idx) => idx !== i));
  const updateLine = (i: number, patch: Partial<Line>) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  const watchedQty      = Form.useWatch('qty',      form);
  const watchedUnitCost = Form.useWatch('unitCost', form);
  const singleTotal     = (Number(watchedQty) || 0) * (Number(watchedUnitCost) || 0);

  const multiTotal = lines.reduce((s, l) => s + (l.qty || 0) * (l.unitCost || 0), 0);
  const total = mode === 'single' ? singleTotal : multiTotal;

  const handleSave = async () => {
    try { await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const v = form.getFieldsValue();
      const id = crypto.randomUUID();
      const now = new Date().toISOString();

      const recordId  = initialValues?.id       ?? id;
      const createdAt = initialValues?.createdAt ?? now;

      let record: any;
      if (mode === 'single') {
        const qty = Number(v.qty || 1);
        const unitCost = Number(v.unitCost || 0);
        record = {
          id: recordId, createdAt,
          supplier: v.supplier || '',
          category: v.category || '',
          description: v.description || 'Единичный расход',
          status: v.status || 'paid',
          qty, unitCost,
          total: qty * unitCost,
          lines: [],
        };
      } else {
        const filledLines = lines.filter(l => l.name.trim());
        if (!filledLines.length) { message.warning('Добавьте хотя бы одну позицию'); setSaving(false); return; }
        record = {
          id: recordId, createdAt,
          supplier: v.supplier || '',
          category: v.category || '',
          description: 'Позиционный расход',
          status: v.status || 'paid',
          total: multiTotal,
          lines: filledLines.map(l => ({
            name: l.name,
            qty: l.qty,
            unitCost: l.unitCost,
            totalCost: l.qty * l.unitCost,
            category: v.category || '',
          })),
        };
      }

      if (initialValues?.id) {
        await apiUpdateExpense(record.id, record);
      } else {
        await apiCreateExpense(record);
      }
      message.success(initialValues?.id ? 'Расход обновлён' : 'Расход добавлен');
      handleClose();
      onCreated();
    } catch (e: any) {
      message.error(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Новый расход"
      width={700}
      footer={
        <Space style={{ justifyContent: 'space-between', width: '100%', display: 'flex' }}>
          <Tag color="red" style={{ fontSize: 14, padding: '4px 12px' }}>
            Итого: {total.toLocaleString('ru-RU')} ֏
          </Tag>
          <Space>
            <Button onClick={handleClose}>Отмена</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>Сохранить</Button>
          </Space>
        </Space>
      }
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Space wrap style={{ width: '100%' }}>
          <Form.Item name="supplier" label="Поставщик" style={{ minWidth: 200 }}>
            <Input placeholder="Название поставщика" />
          </Form.Item>
          <Form.Item name="category" label="Категория" initialValue="Прочее" style={{ minWidth: 180 }}>
            <Select>
              {CATEGORIES.map(c => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="status" label="Статус" initialValue="paid" style={{ minWidth: 160 }}>
            <Select>
              <Option value="paid">Оплачено</Option>
              <Option value="planned">Запланировано</Option>
              <Option value="processing">В процессе</Option>
              <Option value="canceled">Отменено</Option>
            </Select>
          </Form.Item>
        </Space>

        <Divider style={{ margin: '8px 0 12px' }} />

        <Form.Item label="Тип расхода" style={{ marginBottom: 12 }}>
          <Radio.Group value={mode} onChange={e => setMode(e.target.value)}>
            <Radio.Button value="single">Единичный</Radio.Button>
            <Radio.Button value="multi">Позиционный</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {mode === 'single' ? (
          <Space wrap>
            <Form.Item name="description" label="Описание" rules={[{ required: true, message: 'Укажите описание' }]} style={{ minWidth: 260 }}>
              <Input placeholder="На что потрачено" />
            </Form.Item>
            <Form.Item name="qty" label="Кол-во" initialValue={1}>
              <InputNumber min={1} style={{ width: 80 }} onChange={() => form.setFieldValue('_refresh', Date.now())} />
            </Form.Item>
            <Form.Item name="unitCost" label="Сумма (за ед.)" rules={[{ required: true }]}>
              <InputNumber
                min={0} style={{ width: 130 }}
                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                onChange={() => form.setFieldValue('_refresh', Date.now())}
              />
            </Form.Item>
          </Space>
        ) : (
          <>
            {lines.map((line, idx) => (
              <Space key={idx} wrap style={{ marginBottom: 8, background: '#fafafa', padding: 8, borderRadius: 6, width: '100%' }}>
                <Form.Item label="Название" style={{ margin: 0 }}>
                  <Input
                    placeholder="Что куплено"
                    value={line.name}
                    style={{ width: 200 }}
                    onChange={e => updateLine(idx, { name: e.target.value })}
                  />
                </Form.Item>
                <Form.Item label="Кол-во" style={{ margin: 0 }}>
                  <InputNumber min={1} value={line.qty} style={{ width: 75 }} onChange={v => updateLine(idx, { qty: v ?? 1 })} />
                </Form.Item>
                <Form.Item label="Цена" style={{ margin: 0 }}>
                  <InputNumber
                    min={0} value={line.unitCost} style={{ width: 110 }}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                    onChange={v => updateLine(idx, { unitCost: v ?? 0 })}
                  />
                </Form.Item>
                <Form.Item label="Итого" style={{ margin: 0 }}>
                  <Tag color="orange" style={{ fontSize: 13 }}>
                    {((line.qty || 0) * (line.unitCost || 0)).toLocaleString('ru-RU')} ֏
                  </Tag>
                </Form.Item>
                {lines.length > 1 && (
                  <Button danger icon={<DeleteOutlined />} onClick={() => removeLine(idx)} />
                )}
              </Space>
            ))}
            <Button
              icon={<PlusOutlined />}
              onClick={addLine}
              style={{ width: '100%', borderStyle: 'dashed', marginTop: 4 }}
            >
              Добавить позицию
            </Button>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ExpenseModal;
